<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import {
  Menu, Search, Sun, Moon, KeyRound, LogOut, ChevronsLeft, ChevronsRight, X, Star,
} from '@lucide/vue';
import ToastContainer from '@/components/ToastContainer.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import CommandPalette from '@/components/CommandPalette.vue';
import Modal from '@/components/Modal.vue';
import { authApi } from '@/api/authApi';
import { useAuthStore } from '@/stores/auth';
import { useToastStore } from '@/stores/toast';
import { useThemeStore } from '@/stores/theme';
import { NAV_GROUPS, findLink } from '@/navigation';
import { useMediaQuery, TABLET_QUERY } from '@/composables/useMediaQuery';

const auth = useAuthStore();
const toast = useToastStore();
const theme = useThemeStore();
const route = useRoute();
const router = useRouter();

const isTablet = useMediaQuery(TABLET_QUERY);

// Свёрнутый сайдбар (десктоп) — узкая полоса иконок; выбор помним между заходами.
const COLLAPSED_KEY = 'admin.sidebarCollapsed';
function readCollapsed() {
  try { return localStorage.getItem(COLLAPSED_KEY) === '1'; } catch { return false; }
}
const collapsed = ref(readCollapsed());
watch(collapsed, (v) => { try { localStorage.setItem(COLLAPSED_KEY, v ? '1' : '0'); } catch { /* ignore */ } });

// Выезжающее меню на планшете/телефоне.
const drawerOpen = ref(false);
watch(() => route.fullPath, () => { drawerOpen.value = false; });
watch(isTablet, (v) => { if (!v) drawerOpen.value = false; });

const paletteOpen = ref(false);
const userMenuOpen = ref(false);

const groups = computed(() =>
  NAV_GROUPS.map((g) => ({ ...g, links: g.links.filter((l) => auth.can(l.perm)) })).filter((g) => g.links.length),
);
const showLayout = computed(() => route.name !== 'login');
const current = computed(() => findLink(route.path));
const compact = computed(() => collapsed.value && !isTablet.value);
const initials = computed(() => (auth.user?.username ?? '?').slice(0, 2).toUpperCase());

watch(
  () => current.value?.label,
  (label) => { document.title = label ? `${label} · OGTanks Admin` : 'OGTanks Admin'; },
  { immediate: true },
);

async function logout() {
  userMenuOpen.value = false;
  await auth.logout();
  router.push({ name: 'login' });
}

// ---------- Смена собственного пароля ----------
const pwd = reactive({ open: false, current: '', next: '', confirm: '', busy: false });

function openPasswordModal() {
  userMenuOpen.value = false;
  Object.assign(pwd, { open: true, current: '', next: '', confirm: '', busy: false });
}
async function changePassword() {
  if (!pwd.current || !pwd.next) {
    toast.error('Fill in current and new password');
    return;
  }
  if (pwd.next !== pwd.confirm) {
    toast.error('Passwords do not match');
    return;
  }
  pwd.busy = true;
  try {
    await authApi.changeOwnPassword(pwd.current, pwd.next);
    pwd.open = false;
    toast.success('Password changed');
  } finally {
    pwd.busy = false;
  }
}
</script>

<template>
  <template v-if="showLayout">
    <!-- Затемнение под выезжающим меню -->
    <Transition name="fade">
      <div v-if="drawerOpen" class="drawer-scrim" @click="drawerOpen = false" />
    </Transition>

    <aside class="sidebar" :class="{ compact, open: drawerOpen }" aria-label="Main navigation">
      <div class="sb-brand">
        <RouterLink to="/" class="brand" :title="compact ? 'OGTanks Admin' : undefined">
          <span class="brand-mark">
            <svg viewBox="0 0 32 32" aria-hidden="true">
              <rect x="3" y="15" width="26" height="9" rx="4.5" fill="currentColor" opacity=".35" />
              <rect x="7" y="9" width="14" height="8" rx="3" fill="currentColor" />
              <rect x="19" y="11.5" width="11" height="3" rx="1.5" fill="currentColor" />
              <circle cx="9" cy="19.5" r="2" fill="var(--c-surface)" />
              <circle cx="16" cy="19.5" r="2" fill="var(--c-surface)" />
              <circle cx="23" cy="19.5" r="2" fill="var(--c-surface)" />
            </svg>
          </span>
          <span v-if="!compact" class="brand-text">OGTanks <b>Admin</b></span>
        </RouterLink>
        <button v-if="isTablet" class="btn btn-ghost btn-icon btn-sm" aria-label="Close menu" @click="drawerOpen = false">
          <X />
        </button>
      </div>

      <button v-if="!compact" class="sb-search" @click="paletteOpen = true">
        <Search />
        <span>Search…</span>
        <kbd class="hide-mobile">Ctrl K</kbd>
      </button>
      <button v-else class="sb-link sb-search-compact" title="Search (Ctrl K)" @click="paletteOpen = true">
        <Search class="sb-ic" />
      </button>

      <nav class="sb-nav">
        <div v-for="g in groups" :key="g.label" class="sb-group">
          <div v-if="!compact" class="sb-group-label">{{ g.label }}</div>
          <div v-else class="sb-group-sep" />
          <RouterLink
            v-for="link in g.links"
            :key="link.to"
            :to="link.to"
            class="sb-link"
            :title="compact ? link.label : undefined"
          >
            <component :is="link.icon" class="sb-ic" />
            <span v-if="!compact" class="sb-label">{{ link.label }}</span>
          </RouterLink>
        </div>
      </nav>

      <div class="sb-footer">
        <div class="user-chip" :class="{ compact }">
          <button class="user-btn" :title="auth.user?.username" @click="userMenuOpen = !userMenuOpen">
            <span class="avatar">{{ initials }}</span>
            <span v-if="!compact" class="user-meta">
              <span class="user-name truncate">{{ auth.user?.username }}</span>
              <span class="user-role">
                <Star v-if="auth.user?.superadmin" class="star" />
                {{ auth.user?.superadmin ? 'Superadmin' : 'Staff' }}
              </span>
            </span>
          </button>
          <Transition name="pop">
            <div v-if="userMenuOpen" class="user-menu" @mouseleave="userMenuOpen = false">
              <button class="um-item" @click="theme.toggle()">
                <Sun v-if="theme.effective === 'dark'" /><Moon v-else />
                {{ theme.effective === 'dark' ? 'Light theme' : 'Dark theme' }}
              </button>
              <button class="um-item" @click="openPasswordModal"><KeyRound /> Change password</button>
              <div class="um-sep" />
              <button class="um-item danger" @click="logout"><LogOut /> Sign out</button>
            </div>
          </Transition>
        </div>
        <button
          v-if="!isTablet"
          class="btn btn-ghost btn-icon btn-sm collapse-btn"
          :title="collapsed ? 'Expand sidebar' : 'Collapse sidebar'"
          @click="collapsed = !collapsed"
        >
          <ChevronsRight v-if="collapsed" /><ChevronsLeft v-else />
        </button>
      </div>
    </aside>

    <div class="shell">
      <!-- Верхняя панель — только на планшете/телефоне -->
      <header v-if="isTablet" class="topbar">
        <button class="btn btn-ghost btn-icon" aria-label="Open menu" @click="drawerOpen = true"><Menu /></button>
        <div class="topbar-title truncate">
          <component :is="current?.icon" v-if="current" class="tb-ic" />
          {{ current?.label ?? 'OGTanks Admin' }}
        </div>
        <button class="btn btn-ghost btn-icon" aria-label="Search" @click="paletteOpen = true"><Search /></button>
        <button class="btn btn-ghost btn-icon" aria-label="Toggle theme" @click="theme.toggle()">
          <Sun v-if="theme.effective === 'dark'" /><Moon v-else />
        </button>
      </header>
      <main class="main">
        <RouterView />
      </main>
    </div>
  </template>
  <template v-else>
    <RouterView />
  </template>

  <CommandPalette v-model:open="paletteOpen" @logout="logout" />

  <!-- Смена собственного пароля -->
  <Modal :open="pwd.open" title="Change password" subtitle="Use at least 8 characters." width="420px" @close="pwd.open = false">
    <form id="pwd-form" @submit.prevent="changePassword">
      <div class="form-group">
        <label>Current password</label>
        <input v-model="pwd.current" type="password" class="field" autocomplete="current-password" />
      </div>
      <div class="form-group">
        <label>New password</label>
        <input v-model="pwd.next" type="password" class="field" autocomplete="new-password" />
      </div>
      <div class="form-group mb-0">
        <label>Confirm new password</label>
        <input v-model="pwd.confirm" type="password" class="field" autocomplete="new-password" />
      </div>
    </form>
    <template #footer>
      <button class="btn btn-secondary" @click="pwd.open = false">Cancel</button>
      <button class="btn btn-primary" form="pwd-form" type="submit" :disabled="pwd.busy">
        <span v-if="pwd.busy" class="spinner" /> Save password
      </button>
    </template>
  </Modal>

  <ConfirmDialog />
  <ToastContainer />
</template>

<style scoped>
/* ---------- Sidebar ---------- */
.sidebar {
  width: var(--sidebar-w);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  background: var(--c-surface);
  border-right: 1px solid var(--c-border);
  transition: width 0.18s var(--ease);
  z-index: 60;
  min-height: 0;
}
.sidebar.compact { width: var(--sidebar-w-collapsed); }

.sb-brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 60px;
  padding: 0 14px;
  flex-shrink: 0;
}
.sidebar.compact .sb-brand { justify-content: center; padding: 0; }
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--c-text);
  min-width: 0;
}
.brand:hover { text-decoration: none; }
.brand-mark {
  width: 32px;
  height: 32px;
  border-radius: 9px;
  display: grid;
  place-items: center;
  background: linear-gradient(135deg, var(--c-primary), #7c5cff);
  color: #fff;
  flex-shrink: 0;
  box-shadow: 0 4px 14px rgba(79, 140, 255, 0.35);
}
.brand-mark svg { width: 22px; height: 22px; }
.brand-text { font-size: 15px; font-weight: 500; letter-spacing: -0.01em; white-space: nowrap; }
.brand-text b { font-weight: 700; }

.sb-search {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 2px 12px 8px;
  height: 34px;
  padding: 0 10px;
  border-radius: var(--radius);
  border: 1px solid var(--c-border);
  background: var(--c-bg-subtle);
  color: var(--c-text-3);
  font: inherit;
  font-size: var(--fs-sm);
  cursor: pointer;
  flex-shrink: 0;
}
.sb-search:hover { border-color: var(--c-border-strong); color: var(--c-text-2); }
.sb-search svg { width: 15px; height: 15px; }
.sb-search kbd { margin-left: auto; }
.sb-search-compact { margin: 2px auto 4px; }

.sb-nav { flex: 1; overflow-y: auto; padding: 0 10px 12px; overscroll-behavior: contain; }
.sidebar.compact .sb-nav { padding: 0 10px 12px; }
.sb-group { margin-top: 10px; }
.sb-group-label {
  padding: 6px 10px 4px;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--c-text-3);
}
.sb-group-sep { height: 1px; background: var(--c-border-soft); margin: 8px 6px; }
.sb-link {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  padding: 0 10px;
  border-radius: var(--radius);
  color: var(--c-text-2);
  font-size: var(--fs-md);
  font-weight: 500;
  position: relative;
  border: none;
  background: none;
  cursor: pointer;
  font-family: inherit;
  width: 100%;
  transition: background-color 0.12s, color 0.12s;
}
.sidebar.compact .sb-link { justify-content: center; padding: 0; width: 44px; margin: 0 auto 2px; }
.sb-link:hover { background: var(--c-hover); color: var(--c-text); text-decoration: none; }
.sb-link.router-link-active { background: var(--c-primary-soft); color: var(--c-text); }
.sb-link.router-link-active .sb-ic { color: var(--c-primary-text); }
.sb-link.router-link-active::before {
  content: '';
  position: absolute;
  left: -10px;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--c-primary);
}
.sb-ic { width: 17px; height: 17px; flex-shrink: 0; }
.sb-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.sb-footer {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid var(--c-border);
  flex-shrink: 0;
}
.sidebar.compact .sb-footer { flex-direction: column; padding: 10px 0; }
.user-chip { position: relative; flex: 1; min-width: 0; }
.user-chip.compact { flex: none; }
.user-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 6px;
  border-radius: var(--radius);
  border: none;
  background: none;
  color: var(--c-text);
  font: inherit;
  cursor: pointer;
  text-align: left;
}
.user-btn:hover { background: var(--c-hover); }
.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  background: var(--c-violet-soft);
  color: var(--c-violet);
  font-size: 12px;
  font-weight: 700;
}
.user-meta { display: flex; flex-direction: column; min-width: 0; line-height: 1.3; }
.user-name { font-size: var(--fs-sm); font-weight: 600; }
.user-role { font-size: var(--fs-xs); color: var(--c-text-3); display: inline-flex; align-items: center; gap: 4px; }
.star { width: 11px; height: 11px; color: var(--c-warning-text); fill: currentColor; }

.user-menu {
  position: absolute;
  left: 0;
  bottom: calc(100% + 6px);
  min-width: 210px;
  padding: 6px;
  background: var(--c-surface);
  border: 1px solid var(--c-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  z-index: 70;
}
.um-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border-radius: var(--radius-sm);
  border: none;
  background: none;
  color: var(--c-text);
  font: inherit;
  font-size: var(--fs-sm);
  cursor: pointer;
  text-align: left;
}
.um-item:hover { background: var(--c-hover); }
.um-item svg { width: 15px; height: 15px; color: var(--c-text-2); }
.um-item.danger { color: var(--c-danger-text); }
.um-item.danger svg { color: var(--c-danger-text); }
.um-sep { height: 1px; background: var(--c-border); margin: 6px 0; }

/* ---------- Main ---------- */
.shell { flex: 1; min-width: 0; display: flex; flex-direction: column; min-height: 0; }

.topbar {
  height: var(--topbar-h);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  padding-top: env(safe-area-inset-top);
  background: color-mix(in srgb, var(--c-surface) 88%, transparent);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--c-border);
  z-index: 40;
}
.topbar-title {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: var(--fs-lg);
  padding: 0 4px;
}
.tb-ic { width: 18px; height: 18px; color: var(--c-primary-text); flex-shrink: 0; }

/* ---------- Drawer (≤1024) ---------- */
.drawer-scrim {
  position: fixed;
  inset: 0;
  background: var(--c-overlay);
  z-index: 55;
}
@media (max-width: 1024px) {
  .sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: min(300px, 86vw);
    transform: translateX(-100%);
    transition: transform 0.22s var(--ease);
    box-shadow: var(--shadow-lg);
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
  }
  .sidebar.open { transform: none; }
  .sb-link { height: 40px; }
}

.fade-enter-active, .fade-leave-active { transition: opacity 0.2s var(--ease); }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.pop-enter-active, .pop-leave-active { transition: opacity 0.12s, transform 0.12s var(--ease); }
.pop-enter-from, .pop-leave-to { opacity: 0; transform: translateY(4px); }
</style>
